import { getArticle, getArticleError, getArticleLoaded } from '@/redux/modules/article/article.selectors';
import { getItemSummaryCatalogId, getItemSummaryIsDeleted } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSlugItemId } from '@/redux/modules/priceResults/priceResults.selectors';
import { GlobalState } from '@/redux/store';
import {
    getItemIdFromPath,
    isGuideArticlePage,
    isItemPage,
    isPriceResultsPage,
    stripUrlQueryAndHash,
} from '@/utils/urls';

const isItemPageSoft404 = (path: string, state: GlobalState) => {
    if (!isItemPage(path)) {
        return false;
    }

    const itemId = getItemIdFromPath(path);
    const catalogId = getItemSummaryCatalogId(state, itemId);
    const isDeleted = getItemSummaryIsDeleted(state, itemId);

    return !catalogId || isDeleted;
};

const isPriceResultsPageSoft404 = (path: string, state: GlobalState) => {
    if (!isPriceResultsPage(path)) {
        return false;
    }

    return !getSlugItemId(state);
};

const isGuideArticlePageSoft404 = (path: string, state: GlobalState) => {
    if (!isGuideArticlePage(path)) {
        return false;
    }

    const isLoaded = getArticleLoaded(state);
    const article = getArticle(state);
    const error = getArticleError(state);

    // Clean miss only — unexpected fetch errors stay on the article error UI.
    return isLoaded && !article && !error;
};

/**
 * Soft-404 predicate for item, price-result, and guide article pages.
 * Keeps the original URL and lets SSR/client render NotFound with HTTP 404.
 */
export const isSoft404Page = (path: string, state: GlobalState) =>
    isItemPageSoft404(path, state) || isPriceResultsPageSoft404(path, state) || isGuideArticlePageSoft404(path, state);

/**
 * Normalizes paths to ensure consistent soft-404 matching between
 * SSR (full URLs) and Client (stripped pathnames).
 */
export const normalizeSoft404Path = (path: string = ''): string => {
    let pathname = (stripUrlQueryAndHash(path).replace(/\/+$/, '') || '/').toLowerCase();

    if (pathname === '/en-gb') {
        return '/';
    }

    if (pathname.startsWith('/en-gb/')) {
        pathname = pathname.slice('/en-gb'.length) || '/';
    }

    return pathname;
};

/**
 * Bootstrap soft-404 must only apply while the user remains on the same path.
 * Otherwise SPA navigation after a soft-404 stays trapped on NotFound.
 */
export const isSoft404PathActive = (soft404Path: string | undefined, currentPath: string): boolean => {
    if (!soft404Path) {
        return false;
    }

    return normalizeSoft404Path(soft404Path) === normalizeSoft404Path(currentPath);
};
